import React from "react";
import { Link } from "react-router-dom";
import { Gift, Trophy, ChevronDown } from "lucide-react";
import { CODE } from "../mock";
import Particles from "./Particles";

/* First jagged \"cut\" — sits at the bottom of the hero, leading into the duel scene */
export const CutEdge = ({ fill = "#141827", className = "" }) => (
  <svg
    className={`block w-full ${className}`}
    viewBox="0 0 1440 130"
    preserveAspectRatio="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0,70 L60,42 130,78 210,30 290,66 370,22 470,74 560,38 660,82 760,34 850,70 950,26 1050,74 1140,42 1230,82 1320,38 1440,64 L1440,130 L0,130 Z"
      fill={fill}
    />
  </svg>
);

const Hero = () => {
  const scrollDown = () => {
    document.getElementById("duel-scene")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden hero-bg">
      {/* ambient green glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full opacity-[0.16] blur-[120px] bg-[#3ddc84]" />
        <div className="absolute bottom-[10%] left-[8%] w-[300px] h-[300px] rounded-full opacity-[0.07] blur-[90px] bg-[#3ddc84]" />
        <div className="absolute bottom-[18%] right-[6%] w-[260px] h-[260px] rounded-full opacity-[0.06] blur-[80px] bg-[#6cf2a8]" />
      </div>
      {/* noise texture */}
      <div className="absolute inset-0 noise-overlay pointer-events-none" />

      <div className="relative flex-1 flex flex-col items-center justify-center px-6 pt-28 pb-40 text-center">
        {/* Big brand wordmark */}
        <h1
          className="hero-title font-display font-extrabold italic leading-none tracking-tight animate-hero-in"
          data-testid="hero-title"
        >
          <span className="text-white">WA</span>
          <span className="hero-x">X</span>
          <span className="text-white">UM</span>
        </h1>

        <p
          className="mt-7 max-w-xl mx-auto uppercase text-[13px] sm:text-sm font-bold tracking-[0.22em] text-[#aeb7cc] leading-relaxed animate-hero-in-delayed"
          data-testid="hero-tagline"
        >
          Enjoy premium rewards & VIP perks every day
          <br />
          for playing under the code{" "}
          <span className="text-[#3ddc84]">“{CODE}”</span>
        </p>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-4 animate-hero-in-delayed2">
          <a
            href="#bonuses"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("bonuses")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="btn-duel inline-flex items-center gap-2.5 px-8 py-3.5 text-sm"
            data-testid="hero-claim-bonuses"
          >
            <Gift size={17} strokeWidth={2.5} />
            Claim Bonuses
          </a>
          <Link
            to="/leaderboard"
            className="btn-slate inline-flex items-center gap-2.5 px-8 py-3.5 text-sm"
            data-testid="hero-leaderboard"
          >
            <Trophy size={17} strokeWidth={2.5} />
            Leaderboard
          </Link>
        </div>

        <div className="mt-16 flex flex-col items-center gap-3 animate-hero-in-delayed2">
          <p className="uppercase text-xs font-bold tracking-[0.25em] text-[#7e879c]">
            Start earning <span className="text-[#3ddc84]">rewards</span>
          </p>
          <button
            onClick={scrollDown}
            className="w-10 h-10 rounded-full border border-[#39415a] flex items-center justify-center text-[#97a1b8] hover:text-[#3ddc84] hover:border-[#3ddc84] transition-colors duration-200 animate-bounce-soft"
            data-testid="hero-scroll-down"
            aria-label="Scroll down"
          >
            <ChevronDown size={18} />
          </button>
        </div>
      </div>

      {/* green particles rising smoothly out of the cut */}
      <div className="absolute bottom-0 left-0 right-0 h-[60%] pointer-events-none overflow-hidden">
        <Particles count={24} />
      </div>

      {/* the first CUT — jagged silhouette leading into the duel scene */}
      <div className="absolute bottom-0 left-0 right-0">
        <CutEdge fill="#12151f" className="h-[90px] sm:h-[120px]" />
      </div>
    </section>
  );
};

export default Hero;
